// Policy type catalog — defaults + user-defined custom types persisted in localStorage.
// Originally inline in deskte-full.jsx; extracted here so feature components
// (Policies, Commissions, Resources, Imports, Filters) can import without cycles.

// Que estados civiles conservan a la pareja. Vive en su propio modulo sin
// dependencias para que lo puedan importar tambien marketplaceHousehold.js y el
// formulario de la ficha sin arrastrar este catalogo.
import { tienePareja } from './estadoCivil.js';

const DEFAULT_POLICY_TYPES = [
  // "Salud", no "ACA / Marketplace" (Lissette 2026-08-18). El id "aca" NO se
  // toca: es la llave de la base, de type_details y de las coberturas.
  // De paso queda igual que el servidor, que ya llamaba Salud a este ramo
  // en los reportes (utils/policyLabels.ts: HEALTH -> Salud).
  { id:"aca",       label:"Health",                labelEs:"Salud",                  icon:"🏥", color:"#3b82f6" },
  { id:"ma",        label:"Medicare Advantage",      labelEs:"Medicare Advantage",     icon:"⭐", color:"#8b5cf6" },
  { id:"medsup",    label:"Medicare Supplement",     labelEs:"Medicare Suplementario", icon:"💊", color:"#6366f1" },
  { id:"partd",     label:"Medicare Part D",         labelEs:"Medicare Parte D",       icon:"💉", color:"#a78bfa" },
  { id:"life",      label:"Life Insurance",          labelEs:"Seguro de Vida",         icon:"🛡️", color:"#10b981" },
  { id:"final",     label:"Final Expense",           labelEs:"Gastos Finales",         icon:"🌹", color:"#059669" },
  { id:"dental",    label:"Dental",                  labelEs:"Dental",                 icon:"🦷", color:"#f59e0b" },
  { id:"vision",    label:"Vision",                  labelEs:"Visión",                 icon:"👁️", color:"#f97316" },
  { id:"accident",  label:"Accident",                labelEs:"Accidentes",             icon:"🩹", color:"#ef4444" },
  // Hospitalizacion (indemnizacion hospitalaria) y Liability (responsabilidad
  // civil) — Lissette 2026-08-25. La base no tiene un enum propio para ninguno
  // de los dos: se guardan como HEALTH y COMMERCIAL respectivamente y el id
  // exacto viaja en type_details.frontType, igual que Parte D o Suplementario
  // (ver ROUND_TRIP_LOSSY_TYPES mas abajo). Por eso NO van en KIT_TYPE_IDS.
  { id:"hosp",      label:"Hospital Indemnity",      labelEs:"Hospitalización",        icon:"🏨", color:"#2563eb" },
  // Short Term Medical (Lissette 2026-08-26). Cobertura temporal, por meses,
  // que NO es del Marketplace: no lleva subsidio, excluye preexistentes y se
  // renueva —o no— al vencer el termino. Como Hospitalizacion o Parte D, la
  // base no tiene enum propio: se guarda como HEALTH y el id exacto viaja en
  // type_details.frontType (ver ROUND_TRIP_LOSSY_TYPES). Sin migracion.
  { id:"shortterm", label:"Short Term Medical",      labelEs:"Short Term (Temporal)",  icon:"⏳", color:"#7c3aed" },
  { id:"suppl",     label:"Supplemental",            labelEs:"Suplementario",          icon:"➕", color:"#ec4899" },
  // "Memberships" en los dos idiomas (Lissette, 2026-08-28). El id sigue siendo
  // "referred" A PROPOSITO: es lo que hay guardado en las polizas que ya
  // existen, y cambiarlo las dejaria sin tipo. Aqui solo cambia la etiqueta.
  { id:"referred",  label:"Memberships",             labelEs:"Memberships",            icon:"🔗", color:"#64748b" },
  { id:"home",      label:"Homeowners",              labelEs:"Propietarios",           icon:"🏠", color:"#0891b2", pc:true },
  { id:"auto",      label:"Auto Insurance",          labelEs:"Seguro de Auto",         icon:"🚗", color:"#0284c7", pc:true },
  { id:"renters",   label:"Renters",                 labelEs:"Inquilinos",             icon:"🏢", color:"#0369a1", pc:true },
  { id:"commercial",label:"Commercial",              labelEs:"Comercial",              icon:"🏗️", color:"#075985", pc:true },
  { id:"umbrella",  label:"Umbrella",                labelEs:"Paraguas",               icon:"☂️", color:"#0c4a6e", pc:true },
  { id:"liability", label:"Liability",               labelEs:"Responsabilidad Civil",  icon:"⚖️", color:"#1d4ed8", pc:true },
  { id:"pet",       label:"Pet Insurance",           labelEs:"Seguro de Mascotas",     icon:"🐶", color:"#84cc16" },
  { id:"travel",    label:"Travel Insurance",        labelEs:"Seguro de Viaje",        icon:"✈️", color:"#14b8a6" },
];

// ── DONDE SE GUARDA CADA RAMO, Y COMO SE LEE ───────────────────────────────
//
// Estas dos tablas son EL UNICO sitio donde vive la traduccion entre el ramo de
// pantalla y el enum de la base. Estaban copiadas en tres archivos —`api.js`, el
// constructor de reportes y el importador del servidor— y cada copia se habia
// quedado corta por su lado: a la del constructor de reportes le faltaban
// `hosp`, `shortterm` y `liability` desde el dia en que se crearon. Un ramo que
// no esta en el mapa no da error: filtra mal, y en silencio. Mismo patron que
// `policyStatuses.js`, que ya saco de aqui las dos tablas de ESTADOS.
//
// Lo que hace falta entender antes de tocarlas: el enum de la base NO tiene un
// valor por ramo. SEIS ramos del catalogo se guardan como `HEALTH`, y el ramo
// exacto viaja en `type_details.frontType`. Por eso `type = HEALTH` no quiere
// decir "Salud": quiere decir "Salud, Part D, Suplementario, Memberships,
// Hospitalizacion o Short Term" — que es exactamente lo que reporto Wendy
// Cercio el 2026-09-10 al ver Suplementarios dentro de un filtro de SALUD.
// El espejo en el servidor es `deskte-api/src/utils/tiposDePoliza.ts`.

/** El enum con el que se GUARDA cada ramo del catalogo. */
export const TIPO_A_ENUM = {
  aca: 'HEALTH', ma: 'MEDICARE_ADVANTAGE', medsup: 'MEDICARE_SUPPLEMENT',
  partd: 'HEALTH', suppl: 'HEALTH', referred: 'HEALTH', hosp: 'HEALTH',
  shortterm: 'HEALTH',
  life: 'LIFE', final: 'LIFE',
  dental: 'DENTAL', vision: 'VISION', accident: 'DISABILITY',
  home: 'HOME', auto: 'AUTO', renters: 'RENTERS',
  commercial: 'COMMERCIAL', liability: 'COMMERCIAL',
  umbrella: 'UMBRELLA', pet: 'PET', travel: 'TRAVEL',
};

/** El ramo con el que se LEE cada enum cuando la fila no dice otra cosa. Las
 *  cuatro ultimas no tienen ramo propio en el catalogo, asi que la ficha las
 *  ensena como Propietarios, Vida o Comercial. */
export const ENUM_A_TIPO = {
  HEALTH: 'aca', MEDICARE_ADVANTAGE: 'ma', MEDICARE_SUPPLEMENT: 'medsup',
  LIFE: 'life', DENTAL: 'dental', VISION: 'vision', DISABILITY: 'accident',
  HOME: 'home', AUTO: 'auto', RENTERS: 'renters', COMMERCIAL: 'commercial',
  UMBRELLA: 'umbrella', PET: 'pet', TRAVEL: 'travel',
  FLOOD: 'home', LONG_TERM_CARE: 'life', ANNUITY: 'life',
  WORKERS_COMP: 'commercial',
};

/** Palabras que nunca fueron ids pero SI estan selladas en filas reales: el
 *  asistente de migracion de Monday escribia `health` y `medicare`. */
export const ALIAS_ANTIGUOS = { health: 'aca', medicare: 'aca' };

/** True si `v` es un valor del enum de la base (`HEALTH`) y no un ramo de
 *  pantalla (`aca`). Lo llevan dentro los reportes y tableros GUARDADOS antes
 *  de que el desplegable mandara el ramo exacto. */
export function esValorDeEnum(v) {
  return Object.prototype.hasOwnProperty.call(ENUM_A_TIPO, String(v ?? ''));
}

// Legacy: custom types used to live in this browser-only key. We still read it
// ONCE for a one-time migration into AgencySetting (see deskte-full app start);
// nothing writes it anymore.
const LEGACY_CUSTOM_POLICY_TYPES_KEY = "dkt6_custom_policy_types";
export function legacyLocalCustomTypes() {
  try {
    const raw = localStorage.getItem(LEGACY_CUSTOM_POLICY_TYPES_KEY);
    const list = raw ? JSON.parse(raw) : [];
    return Array.isArray(list) ? list.map((p) => ({ ...p, custom: true })) : [];
  } catch { return []; }
}

// Mutable in-memory list — defaults + agency custom types. Stays a live array so
// every importer keeps working by reference. The custom tail is hydrated from
// AgencySetting.policyTypes at app start via setCustomPolicyTypes(); add/remove
// mutate it in place and the CALLER persists to the server
// (apiUpdateAgencySettings), mirroring custom policy statuses.
export const POLICY_TYPES = [...DEFAULT_POLICY_TYPES];

// Replace the entire custom tail with `list` (the built-in defaults are never
// touched). Called at app start from agency settings and after add/remove.
export function setCustomPolicyTypes(list) {
  for (let i = POLICY_TYPES.length - 1; i >= 0; i--) {
    if (POLICY_TYPES[i].custom) POLICY_TYPES.splice(i, 1);
  }
  (Array.isArray(list) ? list : []).forEach((pt) => {
    if (pt && pt.id) POLICY_TYPES.push({ ...pt, custom: true });
  });
}

// The current custom types — what the caller persists to the server.
export function getCustomPolicyTypes() {
  return POLICY_TYPES.filter((p) => p.custom).map((p) => ({ ...p }));
}

export function addCustomPolicyType(pt) {
  POLICY_TYPES.push({ ...pt, custom: true });
}

export function removeCustomPolicyType(id) {
  const idx = POLICY_TYPES.findIndex((p) => p.id === id && p.custom);
  if (idx >= 0) POLICY_TYPES.splice(idx, 1);
}

export const CUSTOM_ICON_CHOICES = ["🏥","⭐","💊","💉","🛡️","🌹","🦷","👁️","🩹","➕","🔗","🏠","🚗","🏢","🏗️","☂️","🐶","✈️","📄","💼","🎯","🏦","⚖️","🔐","🎓","👶","🐾","🚤","🏍️","🚜","🛩️","💎","🧾","📋","🔔","💰","🏆","🌟","🔧"];

export const getPolicyType = (id) => POLICY_TYPES.find(p => p.id === id) || POLICY_TYPES[0];

// The policy-type ids a backend policy can resolve to for welcome-kit template
// lookup — mirror of POLICY_ENUM_TO_SHORT in deskte-api/src/routes/policies.routes.ts.
// The round-trip-lossy types (partd/suppl/referred/final) collapse to a sibling
// enum on read, so a kit keyed to them would never fire; they're intentionally
// excluded so the per-type kit editor only offers types whose template applies.
export const KIT_TYPE_IDS = [
  "aca", "ma", "medsup", "life", "dental", "vision", "accident",
  "home", "auto", "renters", "commercial", "umbrella", "pet", "travel",
];

/**
 * LOS TIPOS QUE PUEDEN LLEVAR KIT DE BIENVENIDA: los de `KIT_TYPE_IDS` **más los
 * que la agencia se creó**.
 *
 * Wendy Cercio (Insurance Latino), 2026-09-11: *"en GESTIONAR TIPOS DE POLIZA
 * creé 1 (Seguro Privado) y 2 (Internacional)… cuando entro a PROSPECTOS Y
 * REFERIDOS no aparece ninguna de las dos"*. Los tipos propios entraron aquí
 * entonces, y siguen: un kit escrito para "Internacional" sí se usa.
 *
 * Y DESDE EL 2026-10-16, TAMBIÉN LOS SIETE QUE ANTES SE DEJABAN FUERA (Parte D,
 * Gastos Finales, Hospitalización, Short Term, Suplementario, Memberships y
 * Responsabilidad Civil). Se excluían porque al guardarse colapsan a un enum
 * hermano y el servidor buscaba el mensaje por ese enum, así que un kit escrito
 * para ellos no salía nunca. Pero eso dejó de ser verdad el 2026-09-11: el
 * servidor busca el mensaje por el RAMO EXACTO (`claveDeKitDePoliza`, que lee
 * `typeDetails.frontType`). Seguir excluyéndolos dejaba a una agencia de
 * Medicare sin forma de escribir el kit de Parte D, y sus renovaciones salían
 * con el mensaje por defecto (agencia de Josep, Lissette 2026-10-16:
 * *"le llega el mensaje default, no se está enviando por tipo de póliza"*).
 *
 * Los de `KIT_TYPE_IDS` van primero, en el mismo orden de siempre; detrás, los
 * otros siete de fábrica; al final, los de la agencia.
 *
 * Se devuelve calculado y NO como constante de módulo: el catálogo propio se
 * hidrata desde los ajustes de la agencia en el arranque (`setCustomPolicyTypes`),
 * así que una lista congelada al importar el archivo saldría siempre vacía.
 */
export function tiposDePolizaConKit() {
  const deFabrica = POLICY_TYPES.filter((p) => !p.custom).map((p) => p.id);
  return [
    ...KIT_TYPE_IDS,
    ...deFabrica.filter((id) => !KIT_TYPE_IDS.includes(id)),
    ...POLICY_TYPES.filter((p) => p.custom).map((p) => p.id),
  ];
}

/**
 * TODOS los tipos que alguien puede ELEGIR: los de fábrica y los de la agencia.
 *
 * Lissette, 2026-09-16: *"en prospecto y referido cuando le das para escoger el
 * tipo de póliza no están saliendo todas ni las personalizadas, ojo con eso"*.
 *
 * Faltaban SIETE de fábrica, y por un motivo que no era de esta pantalla:
 * Prospecto y Referido reusaba la lista del editor de kits, donde esos siete
 * están excluidos a propósito porque su kit no se dispararía (ver arriba). Pero
 * "¿de qué es este prospecto?" no es la misma pregunta que "¿a qué tipo le
 * escribo un kit?": un prospecto de **Parte D**, de **Gastos Finales** o de
 * **Hospitalización** es de lo más normal, y no poder decirlo obliga a apuntarlo
 * como otra cosa.
 *
 * Por eso la lista genérica —la del nombre genérico— es la ANCHA, y la del kit
 * lleva el nombre de lo que de verdad es. Reusar la estrecha fue el error;
 * `tiposPropiosEnLasPantallas.test.ts` vigila que no vuelva.
 */
export function tiposDePolizaParaElegir() {
  return POLICY_TYPES.map((p) => p.id);
}

/** Solo los que creó la agencia. */
export function tiposPropiosDeLaAgencia() {
  return POLICY_TYPES.filter((p) => p.custom).map((p) => ({ ...p }));
}


// Kits que NO son de un tipo de poliza. Comparten una sola regla y por eso
// viven juntos: un documento marcado en uno de estos NO forma parte del kit de
// bienvenida, y por lo tanto no viaja cuando se vende una poliza.
//
//   portal      → el cliente lo ve en su portal.
//   life_change → kit de cambio de vida: los papeles que hay que pedirle a
//                 alguien que se casa, tiene un hijo, se muda o pierde su
//                 trabajo (Lissette 2026-08-19, punto 8 de su lista).
//
// Se guardan aqui para que la lista de "en que kits va este documento", el
// editor del mensaje y el backend hablen del mismo id. Si se agrega otro kit
// que no sea de poliza, va en esta lista Y en NON_POLICY_KITS del backend
// (documents.routes.ts) — si no, marcarlo pondria welcomeKit=true y el
// documento se colaria en el kit de bienvenida.
export const KIT_PORTAL = "portal";
export const KIT_LIFE_CHANGE = "life_change";
export const NON_POLICY_KITS = [KIT_PORTAL, KIT_LIFE_CHANGE];

// Clave donde vive el mensaje del Kit de Cambio de Vida para un tipo de seguro.
// "general" (o vacio) usa el mensaje comun; un tipo usa el suyo y, si no lo
// tiene escrito, el que envia cae al comun. Se guarda en el mismo mapa
// welcomeKitTemplates, asi que un tipo nuevo no necesita migracion.
export const lifeChangeKitKey = (tipo) => (
  tipo && tipo !== "general" ? `${KIT_LIFE_CHANGE}__${tipo}` : KIT_LIFE_CHANGE
);

// Etiqueta de un kit que no es de poliza (para pintarlo en las listas).
export const nonPolicyKitLabel = (id, es) => (
  id === KIT_PORTAL
    ? (es ? "\ud83c\udf10 Kit Portal (lo ve el cliente)" : "\ud83c\udf10 Kit Portal (client sees it)")
    : id === KIT_LIFE_CHANGE
      ? (es ? "\ud83d\udd04 Kit de Cambio de Vida" : "\ud83d\udd04 Life Change Kit")
      : id
);

// Helper: generates the coverage-toggle list from POLICY_TYPES (includes custom types)
export const getCoverageList = () => POLICY_TYPES.map(pt => ({
  key: "cov_" + pt.id,
  icon: pt.icon,
  es: pt.labelEs || pt.label,
  en: pt.label,
  pc: !!pt.pc,
  health: pt.id === "aca" || pt.id === "medsup" || pt.id === "ma" || pt.id === "partd",
}));

// Legacy fixed coverage keys (pre-custom-types). Newer records use "cov_<typeId>".
export const LEGACY_COV_KEYS = ["covHealth","covDental","covVision","covLife","covAuto","covHome","covMedicare","covSupp"];

// Single source of truth for "is this person insured": a member counts as
// insured ONLY when at least one coverage toggle is actually ON. We never
// default an undefined flag to insured — that's what made the modal show
// people as covered when they weren't.
export const memberHasCoverage = (m) => {
  if (!m || typeof m !== "object") return false;
  if (LEGACY_COV_KEYS.some(k => m[k])) return true;
  return Object.keys(m).some(k => k.startsWith("cov_") && m[k]);
};

// Single source of truth for "does this client have a CURRENT spouse to show".
// A spouse only exists while the client's marital status KEEPS a partner AND
// there's a name on file. This mirrors the ClientForm, which only shows/edits
// the spouse block for those statuses. Anything that lists or counts the spouse
// must use this — otherwise a spouse that was removed (marital status flipped
// back to single) but whose data still lingers in c.spouse re-appears as a
// "phantom Cónyuge" on policies/coverage (Marivy 2026-07-28: Tulio Linares).
//
// "Keeps a partner" son DOS estados civiles, no uno (ver crm-app/estadoCivil.js):
// esto exigia "married" a secas, asi que una PAREJA DE HECHO entraba en las
// casillas del conyuge y no aparecia en ningun sitio — ni en el hogar, ni en las
// personas asegurables de la poliza, ni en los cumpleanos — y el siguiente
// guardado la borraba. Por eso los importadores escribian "casado" a la fuerza
// (Lissette, 2026-09-15, la importacion de la agencia de Denisse).
export const hasCurrentSpouse = (c) =>
  !!(c && tienePareja(c.maritalStatus) && c.spouse && c.spouse.firstName);

// Live count of insured household members, derived purely from coverage.
// Primary counts only if covered; spouse only if married + named + covered;
// dependents only if named, covered, and not split into a separate profile.
export const countInsuredMembers = (c) => {
  if (!c) return 0;
  let count = memberHasCoverage(c) ? 1 : 0;
  if (hasCurrentSpouse(c) && memberHasCoverage(c.spouse)) count++;
  (c.dependents || []).forEach(d => { if (d.firstName && !d.separateProfile && memberHasCoverage(d)) count++; });
  return count;
};

// Insured-member count BROKEN DOWN BY POLICY TYPE, from each member's cov_<type>
// toggles. Same membership rule as countInsuredMembers (primary always; spouse
// only if married + named; dependents only if named and not a separate profile).
// Returns a plain object { typeId: count }, e.g. { aca: 3, dental: 1 }.
// Lissette 2026-06-23: the single "Miembros Asegurados" total didn't move when
// she added Vision/Dental to people already insured on ACA (a person is counted
// once regardless of how many coverages) — she wanted the count PER policy type.
export const countInsuredByType = (c) => {
  if (!c) return {};
  const out = {};
  const tally = (m) => {
    if (!m || typeof m !== "object") return;
    Object.keys(m).forEach((k) => {
      if (k.startsWith("cov_") && m[k]) { const t = k.slice(4); out[t] = (out[t] || 0) + 1; }
    });
  };
  tally(c);
  if (hasCurrentSpouse(c)) tally(c.spouse);
  (c.dependents || []).forEach((d) => { if (d.firstName && !d.separateProfile) tally(d); });
  return out;
};

// The coverage-toggle key for a policy's (short) type id, e.g. "aca" → "cov_aca".
// Coverage toggles and policy types share the same short vocabulary, so this is
// a clean 1:1 link between "Daniel is on this ACA policy" and "Daniel's ACA
// coverage toggle is ON".
export const coverageKeyForPolicyType = (type) => "cov_" + (type || "aca");

// Policy → Coverage (ADDITIVE ONLY). Given the client and a just-saved policy's
// short type + its insured-people list (["primary","spouse",<depKey>...]),
// return a PARTIAL patch that turns ON the matching coverage toggle for each
// insured member. Returns null when nothing needs to change so the caller can
// skip the network write. NEVER turns a toggle off — unchecking stays manual to
// avoid destructive cross-system cascades.
export const buildCoveragePatchFromPolicy = (client, policyType, policyInsured) => {
  if (!client || !Array.isArray(policyInsured) || policyInsured.length === 0) return null;
  const covKey = coverageKeyForPolicyType(policyType);
  const insured = new Set(policyInsured);
  const patch = {};
  if (insured.has("primary") && !client[covKey]) patch[covKey] = true;
  // Only a current (married + named) spouse gets a coverage patch. A legacy
  // "spouse" key on policyInsured for a client who is no longer married must not
  // resurrect an emptied spouse object (which would still be truthy) — see the
  // phantom-Cónyuge fix (Marivy 2026-07-28).
  if (insured.has("spouse") && hasCurrentSpouse(client) && !client.spouse[covKey]) {
    patch.spouse = { ...client.spouse, [covKey]: true };
  }
  const deps = Array.isArray(client.dependents) ? client.dependents : [];
  let depsChanged = false;
  const nextDeps = deps.map((d, i) => {
    const key = d.id || ("dep" + i);
    if (insured.has(key) && !d[covKey]) { depsChanged = true; return { ...d, [covKey]: true }; }
    return d;
  });
  if (depsChanged) patch.dependents = nextDeps;
  return Object.keys(patch).length ? patch : null;
};

// Policy → Coverage sync for an EDITED policy. Reconciles ONLY the people whose
// membership changed in this edit (added or removed), and ONLY for this policy's
// type. Added → cov_T ON. Removed → cov_T OFF, UNLESS the person is still insured
// on another policy of the same type. Untouched members — and their manually-set
// coverages that don't have a policy yet — are never modified, so the additive
// "mark coverage → auto-create policy" workflow is preserved. This is what makes
// a manual removal in "Editar Póliza" STICK: turning the person off the policy
// turns off their matching coverage so the additive Coverage→Policy auto-sync on
// profile save won't immediately re-add them (Lissette: "el cliente se sale de la
// póliza, deja solo a los hijos"). Returns null when nothing changes.
export const buildCoverageSyncFromPolicyEdit = (client, policyType, prevInsured, nextInsured, otherPoliciesOfType) => {
  if (!client || !policyType) return null;
  const prev = new Set(Array.isArray(prevInsured) ? prevInsured : []);
  const next = new Set(Array.isArray(nextInsured) ? nextInsured : []);
  const added = [...next].filter((k) => !prev.has(k));
  const removed = [...prev].filter((k) => !next.has(k));
  if (!added.length && !removed.length) return null;
  const covKey = coverageKeyForPolicyType(policyType);
  // Keys still insured on another policy of the same type — keep their cov ON.
  const stillInsured = new Set();
  (Array.isArray(otherPoliciesOfType) ? otherPoliciesOfType : []).forEach((p) => {
    (Array.isArray(p.policyInsured) ? p.policyInsured : []).forEach((k) => stillInsured.add(k));
  });
  const turnOn = new Set(added);
  const turnOff = new Set(removed.filter((k) => !stillInsured.has(k)));
  const desiredFor = (key, current) => {
    if (turnOn.has(key)) return true;
    if (turnOff.has(key)) return false;
    return current;
  };
  const patch = {};
  const pDesired = desiredFor("primary", Boolean(client[covKey]));
  if (pDesired !== Boolean(client[covKey])) patch[covKey] = pDesired;
  if (client.spouse) {
    const sDesired = desiredFor("spouse", Boolean(client.spouse[covKey]));
    if (sDesired !== Boolean(client.spouse[covKey])) patch.spouse = { ...client.spouse, [covKey]: sDesired };
  }
  const deps = Array.isArray(client.dependents) ? client.dependents : [];
  let depsChanged = false;
  const nextDeps = deps.map((d, i) => {
    const key = d.id || ("dep" + i);
    const dDesired = desiredFor(key, Boolean(d[covKey]));
    if (dDesired !== Boolean(d[covKey])) { depsChanged = true; return { ...d, [covKey]: dDesired }; }
    return d;
  });
  if (depsChanged) patch.dependents = nextDeps;
  return Object.keys(patch).length ? patch : null;
};

// Enumerate the insurable household members with a stable key that matches the
// PolicyForm chips ("primary", "spouse", dep.id||"dep"+index). Dependents that
// were split into their own profile are excluded — they own their policies.
export const enumerateInsuredMembers = (client) => {
  if (!client) return [];
  const out = [{ key: "primary", member: client, name: `${client.firstName || ""} ${client.lastName || ""}`.trim() }];
  if (client.spouse && client.spouse.firstName) {
    out.push({ key: "spouse", member: client.spouse, name: `${client.spouse.firstName || ""} ${client.spouse.lastName || ""}`.trim() });
  }
  (Array.isArray(client.dependents) ? client.dependents : []).forEach((d, i) => {
    if (d.firstName && !d.separateProfile) {
      out.push({ key: d.id || ("dep" + i), member: d, name: `${d.firstName || ""} ${d.lastName || ""}`.trim() });
    }
  });
  return out;
};

// The cov_<type> keys that are ON for a member, returned as bare type ids
// (e.g. ["aca","dental"]). Skips legacy fixed keys — only the type-aligned
// cov_<typeId> scheme can map cleanly to a policy type.
const coveredTypeIds = (member) => {
  if (!member || typeof member !== "object") return [];
  return Object.keys(member)
    .filter((k) => k.startsWith("cov_") && member[k])
    .map((k) => k.slice(4));
};

// Types the backend collapses into a shared DB enum (partd/suppl/referred →
// HEALTH, final → LIFE) — the schema has no distinct value for them. As of
// 2026-06-23 the front end stamps the exact type into type_details.frontType on
// save and restores it on read (see mapPolicy*Api in api.js), so these now
// round-trip faithfully in the SPA and the Coverage→Policy planners below sync
// them like any other type (Lissette: "solo funciona en ACA, no en las demás").
// The set is kept for documentation and because welcome-kit lookup still keys
// off the BACKEND enum (KIT_TYPE_IDS), which frontType does not affect.
export const ROUND_TRIP_LOSSY_TYPES = new Set(["partd", "suppl", "referred", "final", "hosp", "liability", "shortterm"]);

// Coverage → Policy planner (ADDITIVE ONLY, no side effects). Given the client
// and its existing policies (frontend shape, short `type`), work out what the
// "Sincronizar" button should do so each covered member lands on a policy of
// the matching type:
//   • exactly ONE policy of that type → add the member to its insured list
//   • ZERO policies of that type      → create one empty stub with the member
//   • MORE THAN ONE of that type      → ambiguous: do nothing, report a skip
// Returns { updates:[{policy,newInsured,addedNames}], creates:[{type,keys,names}],
//           skips:[{type,name}] }. Never removes anyone from a policy.
export const planPoliciesFromCoverage = (client, clientPolicies) => {
  const policies = Array.isArray(clientPolicies) ? clientPolicies : [];
  const members = enumerateInsuredMembers(client);
  const updates = new Map();        // policyId -> { policy, insured:Set, addedNames:[] }
  const creates = new Map();        // type -> { type, keys:[], names:[] }
  const skips = [];
  for (const { key, member, name } of members) {
    for (const type of coveredTypeIds(member)) {
      const ofType = policies.filter((p) => p.type === type);
      if (ofType.length === 1) {
        const pol = ofType[0];
        const current = Array.isArray(pol.policyInsured) ? pol.policyInsured : [];
        if (current.includes(key)) continue;
        if (!updates.has(pol.id)) updates.set(pol.id, { policy: pol, insured: new Set(current), addedNames: [] });
        const u = updates.get(pol.id);
        if (!u.insured.has(key)) { u.insured.add(key); u.addedNames.push(name); }
      } else if (ofType.length === 0) {
        if (!creates.has(type)) creates.set(type, { type, keys: [], names: [] });
        const c = creates.get(type);
        if (!c.keys.includes(key)) { c.keys.push(key); c.names.push(name); }
      } else {
        skips.push({ type, name, reason: "ambiguous" });
      }
    }
  }
  return {
    updates: [...updates.values()].map((u) => ({ policy: u.policy, newInsured: [...u.insured], addedNames: u.addedNames })),
    creates: [...creates.values()],
    skips,
  };
};

// Coverage → Policy planner driven by the DELTA between the previous and the
// just-saved client (profile "Editar Cliente" save). For each enumerable member
// we compare which cov_<type> toggles they had BEFORE vs AFTER this save and act
// ONLY on the ones that actually changed:
//   • newly checked (off→on) → add the member to the single policy of that type,
//     or create an empty stub if none exists.
//   • newly unchecked (on→off) → remove the member from the single policy of
//     that type (if they're on it).
// Crucially this is delta-based, NOT a full mirror: members and types the agent
// did NOT touch in this save are left exactly as-is, so an unrelated profile
// edit can never strip people off pre-existing policies (the destructive risk
// behind the #410–#416 saga). Ambiguous types (>1 policy) and round-trip-lossy
// types are skipped. `prevClient` may be null/undefined for a brand-new client —
// then every covered type is treated as newly checked (additive only).
export const planPolicyChangesFromCoverageDelta = (prevClient, nextClient, clientPolicies) => {
  const policies = Array.isArray(clientPolicies) ? clientPolicies : [];
  const members = enumerateInsuredMembers(nextClient);
  const prevByKey = new Map(enumerateInsuredMembers(prevClient || {}).map((m) => [m.key, m.member]));
  const updates = new Map();   // policyId -> { policy, insured:Set, addedNames:[], removedNames:[] }
  const creates = new Map();   // type -> { type, keys:[], names:[] }
  const skips = [];
  const getUpdate = (pol) => {
    if (!updates.has(pol.id)) {
      const cur = Array.isArray(pol.policyInsured) ? pol.policyInsured : [];
      updates.set(pol.id, { policy: pol, insured: new Set(cur), addedNames: [], removedNames: [] });
    }
    return updates.get(pol.id);
  };
  for (const { key, member, name } of members) {
    const nextTypes = new Set(coveredTypeIds(member));
    const prevMember = prevByKey.get(key);
    const prevTypes = new Set(prevMember ? coveredTypeIds(prevMember) : []);
    const added = [...nextTypes].filter((t) => !prevTypes.has(t));
    const removed = [...prevTypes].filter((t) => !nextTypes.has(t));
    for (const type of added) {
      const ofType = policies.filter((p) => p.type === type);
      if (ofType.length === 1) {
        const u = getUpdate(ofType[0]);
        if (!u.insured.has(key)) { u.insured.add(key); u.addedNames.push(name); }
      } else if (ofType.length === 0) {
        if (!creates.has(type)) creates.set(type, { type, keys: [], names: [] });
        const c = creates.get(type);
        if (!c.keys.includes(key)) { c.keys.push(key); c.names.push(name); }
      } else {
        skips.push({ type, name, reason: "ambiguous" });
      }
    }
    for (const type of removed) {
      const ofType = policies.filter((p) => p.type === type);
      if (ofType.length === 1) {
        const u = getUpdate(ofType[0]);
        if (u.insured.has(key)) { u.insured.delete(key); u.removedNames.push(name); }
      } else if (ofType.length > 1) {
        skips.push({ type, name, reason: "ambiguous" });
      }
    }
  }
  return {
    updates: [...updates.values()]
      .filter((u) => u.addedNames.length || u.removedNames.length)
      .map((u) => ({ policy: u.policy, newInsured: [...u.insured], addedNames: u.addedNames, removedNames: u.removedNames })),
    creates: [...creates.values()],
    skips,
  };
};

// ---------------------------------------------------------------------------
// CAMPOS QUE SOLO EXISTEN EN SALUD
//
// Lissette, 2026-08-28: "solamente en la póliza tipo salud vas a dejar número
// de aplicación y subsidio, costo total del plan; los demás tipos de póliza en
// su tarjeta no llevan eso". Y al preguntarle si ocultarlos también del
// formulario de editar y qué hacer con lo ya guardado: "también del formulario
// editar sí; aunque no se vea, déjalo".
//
// LOS DATOS NO SE BORRAN. Una póliza que no es de salud y tenga algo escrito
// ahí lo conserva en la base — simplemente deja de pedirse y de enseñarse.
// Borrarlo sería tirar un dato que alguien escribió a propósito, y no habría
// forma de recuperarlo.
//
// Esta regla SUSTITUYE a dos listas negativas (`NO_SUBSIDY_TYPES`) que estaban
// escritas a mano en dos sitios del mismo archivo — y que **ya se habían
// separado**: la del formulario incluía "shortterm" y la de la tarjeta no, así
// que en una póliza Short Term el campo se pedía en el formulario y no se veía
// en la tarjeta. Un solo lector positivo no puede desalinearse consigo mismo.

/** Los campos que solo tienen sentido en una póliza de Salud (Marketplace). */
export const CAMPOS_SOLO_DE_SALUD = ['applicationNumber', 'subsidy', 'planTotalCost'];

/**
 * ¿Es una póliza de Salud?
 *
 * Solo `aca`. El subsidio y el costo total del plan son conceptos del
 * Marketplace: en dental, visión, vida, auto o mascotas no existen, y el número
 * de aplicación es del trámite del Marketplace. Medicare tampoco los usa.
 */
export const esPolizaDeSalud = (type) => String(type || '') === 'aca';
